import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../api';

// Porción Cosmic/Telefónica de `ClientsView.jsx` -- la rama `activeTab === 'telefonica'` de lo
// que antes era un único `fetchOps` que mezclaba pipeline normal + Cosmic. Ver useClientOps.js
// para el pipeline no-Cosmic.

export function cosmicClientsQueryKey({ status, search, page }) {
    return ['client', 'cosmic', 'clients', { status, search, page }];
}

export function useCosmicClients({ status, search, page, enabled = true }) {
    return useQuery({
        queryKey: cosmicClientsQueryKey({ status, search, page }),
        queryFn: async () => {
            const d = await clientApi.listCosmicClients({ status, search: search || undefined, page, limit: 120 });
            return {
                clients: d.clients || [],
                total: d.total || 0,
                counts: d.counts || { activas: 0, inventario: 0, suspendidas: 0 },
            };
        },
        enabled,
    });
}

export function useCosmicHistory({ search, page, enabled = true }) {
    return useQuery({
        queryKey: ['client', 'cosmic', 'history', { search, page }],
        queryFn: async () => {
            const d = await clientApi.listCosmicHistory({ search: search || undefined, page, limit: 120 });
            return { history: d.history || [], total: d.total || 0 };
        },
        enabled,
    });
}

export const EMPTY_ADINTELLI = { balance: 0, activeLines: 0, suspendedLines: 0, consumptionMB: 0 };

export function useAdintelliDashboard({ enabled = true } = {}) {
    return useQuery({
        queryKey: ['client', 'cosmic', 'adintelli'],
        enabled,
        queryFn: async () => {
            const d = await clientApi.fetchAdintelliDashboard();
            if (!d.success) return EMPTY_ADINTELLI;
            const rawBalance = d.metrics?.balance || '0';
            const cleanBalance = typeof rawBalance === 'string' ? rawBalance.replace(/,/g, '') : rawBalance;
            return {
                balance: parseFloat(cleanBalance) || 0,
                activeLines: d.metrics?.activeLines || 0,
                suspendedLines: d.metrics?.suspendedLines || 0,
                consumptionMB: 0, // Placeholder hasta que lo soporte la API V8 de Adintelli.
            };
        },
    });
}

export function useValidateCosmicLine() {
    return useMutation({ mutationFn: (num) => clientApi.validateCosmicLine(num) });
}

export function useCreateClientNotification() {
    return useMutation({ mutationFn: (payload) => clientApi.createNotification(payload) });
}

// `fetchLiveBenefits` no es una lectura nueva -- parcha filas ya cargadas de la query de
// clientes Cosmic activa (recharge_date llega null hasta que este endpoint la resuelve). Se
// expone como mutación cuyo `onSuccess` recibe el `queryKey` de la página que la disparó y
// parcha esa cache directo, preservando el debounce de 500ms que ya vive en el `useEffect` de
// `ClientsView.jsx` (no se duplica acá).
export function useLiveBenefitsPatch() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ lines }) => clientApi.fetchLiveBenefits(lines),
        onSuccess: (d, { queryKey }) => {
            if (!d.updated || d.updated.length === 0) return;
            queryClient.setQueryData(queryKey, (prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    clients: prev.clients.map((o) => {
                        const upd = d.updated.find((u) => u.msisdn === o.line);
                        return upd ? { ...o, ...upd } : o;
                    }),
                };
            });
        },
    });
}
